import React, { useMemo, useRef, useState } from "react";
import { SURFACE } from "../../../dark-theme/palette";
import Logger from "../../../services/Logger";
import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import CrossGrid from "../../puzzles/crossword/CrossGrid";
import { usePagesTranslation } from "../../locales";

const StaticCrossGrid = ({ puzzleData }) => {
    const { t } = usePagesTranslation();
    // State for found words (initially empty) - must be called before any early returns
    const [wordsFound, setWordsFound] = useState([]);
    const puzzleGridRef = useRef(null);

    // Process puzzle data
    const processedData = useMemo(() => {
        if (!puzzleData) return null;

        // Parse filledCoordinates from template
        let filledCoordinates = [];
        if (puzzleData.template?.filledCoordinates) {
            try {
                filledCoordinates = JSON.parse(puzzleData.template.filledCoordinates);
            } catch (error) {
                Logger.error('Error parsing filledCoordinates:', error);
                filledCoordinates = [];
            }
        }

        // Create grid from letters string
        let grid = [];
        if (puzzleData.letters) {
            const lines = puzzleData.letters.split('\n');
            const paddedLines = lines.slice(0, 12);
            while (paddedLines.length < 12) {
                paddedLines.push('');
            }

            grid = paddedLines.map(line => {
                const chars = line.split('').map(char => char.toUpperCase());
                while (chars.length < 12) {
                    chars.push(' ');
                }
                return chars.slice(0, 12);
            });
        }

        // Parse words from template details
        let words = [];
        if (puzzleData.template?.details?.scores) {
            words = puzzleData.template.details.scores.map(score => score.word || score.item?.word || '').filter(w => w);
        }

        return {
            words,
            filledCoordinates,
            grid,
            title: puzzleData.title || 'Crossword Puzzle',
            type: 'CROSSWORD'
        };
    }, [puzzleData]);

    if (!processedData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No puzzle data available</Typography>
            </Box>
        );
    }

    const progressPercentage = processedData.words.length > 0
        ? Math.round((wordsFound.length / processedData.words.length) * 100)
        : 0;

    // Handle cell click
    const handleClickedWords = (word, cells) => {
        // Static component - no action needed
    };

    // Clear selection
    const handleClearClickedWords = () => {
        puzzleGridRef.current?.clearSelection();
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            p: 2,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
        }}>
            {/* Puzzle Grid */}
            <Box sx={{
                backgroundColor: SURFACE.card,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '650px',
                gap: 2
            }}>
                <Box sx={{ 
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    aspectRatio: '1',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    <CrossGrid
                        ref={puzzleGridRef}
                        grid={processedData.grid}
                        words={processedData.words}
                        wordsFound={wordsFound}
                        onClearClickedWords={handleClearClickedWords}
                        onClickedWords={handleClickedWords}
                        gridSize={12}
                        isFullscreen={false}
                        isBoldFont={false}
                        filledCoordinates={processedData.filledCoordinates}
                        isHintMode={false}
                        highlightCells={[]}
                        hideAllWords={true}
                        clueNumberFontPx={11}
                    />
                </Box>

                {/* Progress Bar */}
                <Box sx={{ width: '100%' }}>
                    <Paper sx={{ p: 2, backgroundColor: SURFACE.sunken }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography
                                variant="subtitle1"
                                color="primary"
                                sx={{
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    fontSize: '0.95rem'
                                }}
                            >
                                Crossword
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    ({processedData.words.length} words)
                                </Box>
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {t("puzzle.playCross.find-all-words")}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPercentage}
                                    sx={{ height: 4, borderRadius: 2, flex: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content', fontWeight: 500 }}>
                                    {wordsFound.length} / {processedData.words.length}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default StaticCrossGrid;

